export type PublicNoticeKind =
  | 'session'
  | 'busy'
  | 'satisfied'
  | 'used'
  | 'invalid'
  | 'need_session'
  | 'need_mailbox'
  | 'need_code'
  | 'failed'
  | 'processing'
  | 'done'
  | 'delinquent'
  | 'account_busy'

export type PublicNoticeTone = 'info' | 'warn' | 'action' | 'fail' | 'ok'

export type PublicNotice = {
  kind: PublicNoticeKind
  tone: PublicNoticeTone
  title: string
  body: string
}

const KIND_TONE: Record<PublicNoticeKind, PublicNoticeTone> = {
  session: 'action',
  busy: 'warn',
  satisfied: 'warn',
  used: 'info',
  invalid: 'fail',
  need_session: 'action',
  need_mailbox: 'action',
  need_code: 'action',
  failed: 'fail',
  processing: 'info',
  done: 'ok',
  delinquent: 'warn',
  account_busy: 'warn',
}

export function payloadNoticeText(data: unknown): string {
  const body = data && typeof data === 'object' ? (data as Record<string, unknown>) : {}
  const nested = body.data && typeof body.data === 'object' ? (body.data as Record<string, unknown>) : {}
  return [body.notice, body.error, body.msg, body.message, body.stage_message, body.last_error, nested.error, nested.msg, nested.message]
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .join('\n')
}

function isFailedStatus(status: string) {
  return ['failed', 'failed_precharge', 'declined', 'cancelled', 'error'].includes(status)
}

export const ACCOUNT_SWITCH_HINT = '账号异常，请更换账号后重试'

function looksAccountBlocked(text: string) {
  const s = String(text || '')
  return /验证码|更换支付|更换账号|账号异常|不支持开通|captcha|not eligible|ineligible/i.test(s)
}

function looksAccountBusy(text: string) {
  const s = String(text || '')
  const lower = s.toLowerCase()
  return (
    /进行中的直充|开通中的订单/.test(s) ||
    (lower.includes('in progress') && (lower.includes('order') || lower.includes('direct')))
  )
}

function looksAlreadySatisfied(text: string) {
  const s = String(text || '')
  const lower = s.toLowerCase()
  return (
    /already satisfied/i.test(lower) ||
    /不能重复购买|当前套餐已满足|到期后才能再次购买|才能再次购买/.test(s) ||
    (/已有/.test(s) && /套餐|plus|pro/i.test(s))
  )
}

function keepSatisfiedCopy(text: string) {
  const s = String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .find((line) => looksAlreadySatisfied(line) && line.length < 120)
  return s || ''
}

export function classifyPublicNotice(input: {
  httpStatus?: number
  notice?: string
  code?: string
  status?: string
  message?: string
}): PublicNoticeKind {
  const notice = String(input.notice || '').trim().toLowerCase()
  const code = String(input.code || '').trim().toUpperCase()
  const status = String(input.status || '').trim().toLowerCase()
  const message = String(input.message || '')
  const lower = message.toLowerCase()
  const http = Number(input.httpStatus || 0)

  if (notice && notice in KIND_TONE) {
    if (notice === 'failed' && /兑换结果不存在|result not found/i.test(message) && !isFailedStatus(status)) {
      return 'processing'
    }
    if (notice === 'failed' && /预检超时/.test(message)) {
      return 'busy'
    }
    if (notice === 'processing' && looksAlreadySatisfied(message)) {
      return 'satisfied'
    }
    if (notice === 'processing' && looksAccountBusy(message)) {
      return 'account_busy'
    }
    if (notice === 'processing' && (isFailedStatus(status) || looksAccountBlocked(message))) {
      return 'failed'
    }
    return notice as PublicNoticeKind
  }

  if (code === 'ALLOCATING' || status === 'allocating' || /提卡超时|分配开通|资源还在准备|系统繁忙/.test(message)) {
    return 'busy'
  }
  if (
    status === 'session' ||
    /session 检查失败|登录状态|会话已失效|会话已过期|invalid session|session expired|请确认登录/.test(lower)
  ) {
    return 'session'
  }
  if (
    status === 'already_satisfied' ||
    looksAlreadySatisfied(message)
  ) {
    return 'satisfied'
  }
  if (looksAccountBusy(message)) {
    return 'account_busy'
  }
  if (status === 'used' || status === 'consumed' || /已使用|已兑换|已消耗/.test(message)) {
    return 'used'
  }
  if (status === 'unused' && /无效|不可用|not found/.test(lower)) {
    return 'invalid'
  }
  if (/请输入卡密|卡密无效|unavailable code|invalid or unavailable/.test(lower)) {
    return 'invalid'
  }
  if (/预检超时/.test(message)) {
    return 'busy'
  }
  if (status === 'failed') {
    return 'failed'
  }
  if (
    /context canceled|timeout|timed out|sql:|no rows|502|504|网络|稍后重试|zovocard|http/.test(lower) ||
    http === 502 ||
    http === 503 ||
    http === 504
  ) {
    return 'busy'
  }
  if (status === 'completed' || status === 'used') {
    return status === 'completed' ? 'done' : 'used'
  }
  if (
    isFailedStatus(status) ||
    looksAccountBlocked(message) ||
    /预扣失败|开通未成功|兑换被拒绝/.test(message)
  ) {
    return 'failed'
  }
  if (['processing', 'queued', 'pending', 'running', 'accepted', 'review', 'reserved', 'requires_action', 'plus_paid', 'funding_pending', 'awaiting_card', 'dispatching'].includes(status) || http === 202) {
    return 'processing'
  }
  if (http >= 400) return 'failed'
  return 'processing'
}

export function publicNoticeTone(kind: PublicNoticeKind): PublicNoticeTone {
  return KIND_TONE[kind]
}

export function buildPublicNotice(
  kind: PublicNoticeKind,
  t: (key: string, params?: Record<string, unknown>) => string,
  params?: Record<string, unknown>,
): PublicNotice {
  return {
    kind,
    tone: publicNoticeTone(kind),
    title: t(`recharge.notice.${kind}Title`, params),
    body: t(`recharge.notice.${kind}Body`, params),
  }
}

export function noticeFromPayload(
  httpStatus: number,
  data: any,
  t: (key: string, params?: Record<string, unknown>) => string,
): PublicNotice {
  const body = data && typeof data === 'object' ? data : {}
  const text = payloadNoticeText(data)
  const kind = classifyPublicNotice({
    httpStatus,
    notice: String(body.notice || body.data?.notice || ''),
    code: String(body.code || body.data?.code || ''),
    status: String(body.status || body.data?.status || ''),
    message: text,
  })
  const notice = buildPublicNotice(kind, t)
  if (kind === 'satisfied') {
    const kept = keepSatisfiedCopy(text)
    if (kept) notice.body = kept
  }
  if (kind === 'failed' && looksAccountBlocked(text)) {
    const hint = text
      .split('\n')
      .map((s) => s.trim())
      .find((s) => looksAccountBlocked(s) && !/^(failed|processing|failed_precharge)$/i.test(s) && s.length < 80)
    if (hint) notice.body = ACCOUNT_SWITCH_HINT
  }
  if (kind === 'busy' && /预检超时/.test(text)) {
    notice.body = t('recharge.notice.busyBody')
  }
  return notice
}
